import {
  Fragment,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  TouchEvent as ReactTouchEvent,
  useCallback,
  useEffect,
  useId,
  useRef
} from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import { useIsPhoneTouch } from 'hooks/useIsPhoneTouch';

import { BottomSheet } from 'components/BottomSheet';
import ForcePortrait from 'components/ForcePortrait/ForcePortrait';
import { ClockIcon } from 'components/SaloonLobby/icons';

import { HEADS_UP_INTRO_COPY, HeadsUpIntroContent } from './headsUpIntroCopy';

import styles from './HeadsUpIntroModal.module.scss';

/**
 * The one-time heads-up Sit & Go introduction (EN-10748).
 *
 * The multi-run introduction (components/MultiRunIntro, EN-10791) in a second launch
 * role, brought in line with the redesigned promo entry modal (EN-11291): the same
 * panel, title, parchment "how it works" card, primary button and fine print, and the
 * drawer rule. On a phone it opens as a bottom drawer (components/BottomSheet: grab
 * handle, swipe-down and tap-outside dismiss, safe areas); on tablet and desktop it is
 * the centered dialog, with focus moved in and trapped, Escape, focus restored on close,
 * and a backdrop tap ignored for the first moments after it opens.
 *
 * What differs from the multi-run intro on purpose (Jason, 2026-10-25): the format is
 * named above the headline and both of its terms are defined in a line each; the steps
 * follow the real journey and carry two replicas of the lobby so the player recognises
 * what they are looking for: the footer's Sit & Go tab (its clock and label, drawn as
 * the tab draws them) and the tab's buy-in section headers (the same decorative face and
 * hairlines), each beside the prize that buy-in plays for; and the one action names its
 * destination, FIND A HEADS-UP TABLE, which lands on the Sit & Go tab.
 *
 * Pure props. What it says arrives as `content` (an edition's copy merged over the
 * timeless copy, resolveHeadsUpIntroContent), rendered as plain text. When it shows and
 * what "seen" means live in the auto-prompt; this component only reports how it was
 * dismissed.
 */
export type HeadsUpIntroDismissReason = 'close' | 'escape' | 'backdrop' | 'find-table';

/**
 * `auto` (default) opens a bottom drawer on a phone and a centered dialog on tablet
 * and desktop (the EN-11291 drawer rule); `drawer` and `dialog` force one or the
 * other, for previews and stories.
 */
export type HeadsUpIntroPresentation = 'auto' | 'drawer' | 'dialog';

export const BACKDROP_TAP_GUARD_MS = 600;

type HeadsUpIntroModalProps = {
  onDismiss: (reason: HeadsUpIntroDismissReason) => void;
  /** The edition's headline, buy-ins, availability and small print, over the timeless copy. */
  content: HeadsUpIntroContent;
  presentation?: HeadsUpIntroPresentation;
  backdropGuardMs?: number;
};

const FOCUSABLE = 'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

// Each entry on a line of its own; the space between them keeps the text whole for a
// screen reader.
const Lines = ({ lines, className }: { lines: readonly string[]; className: string }) => (
  <>
    {lines.map((line, index) => (
      <Fragment key={index}>
        {index > 0 && ' '}
        <span className={className}>{line}</span>
      </Fragment>
    ))}
  </>
);

/** Inside the drawer, a touch on content that is already scrolled scrolls it; only a
 *  touch at the top drags the drawer down. */
const keepScrollFromDragging = (event: ReactTouchEvent<HTMLDivElement>) => {
  if (event.currentTarget.scrollTop > 0) event.stopPropagation();
};

/**
 * The lobby footer's Sit & Go tab, as the footer draws it (SaloonLobby.module.scss
 * `.footerTab` in its active state: the clock icon, the uppercase label, the orange and
 * its glow), on a sliver of the footer's navy. Decorative: the step's text already says
 * "the Sit & Go tab".
 */
const SitGoTabReplica = ({ label }: { label: string }) => (
  <span className={styles.tabReplica} aria-hidden='true'>
    <ClockIcon className={styles.tabReplicaIcon} />
    <span className={styles.tabReplicaLabel}>{label}</span>
  </span>
);

const HeadsUpIntroModal = ({
  onDismiss,
  content,
  presentation = 'auto',
  backdropGuardMs = BACKDROP_TAP_GUARD_MS
}: HeadsUpIntroModalProps) => {
  const copy = HEADS_UP_INTRO_COPY;
  const titleId = useId();
  const isPhone = useIsPhoneTouch();
  const asDrawer = presentation === 'drawer' || (presentation === 'auto' && isPhone);
  const dialogRef = useRef<HTMLDivElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const openedAtRef = useRef(0);
  const dismissedRef = useRef(false);

  // One dismissal only: a click and an Escape in the same beat must not report twice
  // (the prompt writes "seen" and emits analytics on the first).
  const dismiss = useCallback(
    (reason: HeadsUpIntroDismissReason) => {
      if (dismissedRef.current) return;
      dismissedRef.current = true;
      onDismiss(reason);
    },
    [onDismiss]
  );

  // Where focus came from, taken once on mount and given back once on unmount: a phone
  // turned sideways mid-read swaps the drawer for the dialog without losing the control
  // that opened this.
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    openedAtRef.current = Date.now();
    restoreFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return () => {
      const previouslyFocused = restoreFocusRef.current;
      if (previouslyFocused?.isConnected) previouslyFocused.focus({ preventScroll: true });
    };
  }, []);

  // Move focus into whichever presentation is showing.
  useEffect(() => {
    (asDrawer ? drawerRef : dialogRef).current?.focus({ preventScroll: true });
  }, [asDrawer]);

  // Same body scroll lock as the promo modal, scrollbar gutter included.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
    };
  }, []);

  const handleBackdropClick = () => {
    if (Date.now() - openedAtRef.current < backdropGuardMs) return;
    dismiss('backdrop');
  };

  const stopPropagation = (event: ReactMouseEvent) => event.stopPropagation();

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      dismiss('escape');
      return;
    }
    if (event.key !== 'Tab' || !dialogRef.current) return;

    // Keep Tab inside the dialog: wrap from the last control to the first and back, and
    // pull focus in if it starts on the dialog itself.
    const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || active === dialogRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const handleDrawerKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape') return;
    event.stopPropagation();
    dismiss('escape');
  };

  const cardHeadingId = `${titleId}-how`;
  // The dialog is named by the format first, then the headline, so a screen reader
  // hears "Heads-up Sit & Go" before the tagline (headsUpIntroTitle keeps the same order).
  const eyebrowId = `${titleId}-format`;
  const dialogLabel = `${eyebrowId} ${titleId}`;
  const { tiers } = content;

  // In both presentations the lead scrolls and the tail stays pinned, so the action is
  // never below the fold on a short phone or a short laptop screen.
  const lead = (
    <>
      <div className={styles.header}>
        <div className={styles.headerCopy}>
          <p className={styles.eyebrow}>
            <span className={styles.newTag}>{copy.newTag}</span>
            <span id={eyebrowId} className={styles.eyebrowText}>
              {copy.eyebrow}
            </span>
          </p>
          <h2 id={titleId} className={styles.title}>
            <Lines lines={content.titleLines} className={styles.titleLine} />
          </h2>
        </div>
        <button
          type='button'
          className={styles.close}
          aria-label={copy.closeLabel}
          onClick={() => dismiss('close')}>
          <CloseIcon fontSize='inherit' />
        </button>
      </div>

      <dl className={styles.definitions}>
        {copy.definitions.map((definition) => (
          <div key={definition.term} className={styles.definition}>
            <dt className={styles.definitionTerm}>{definition.term}</dt>
            <dd className={styles.definitionText}>{definition.text}</dd>
          </div>
        ))}
      </dl>

      <section className={styles.card} aria-labelledby={cardHeadingId}>
        <span className={styles.cardFrame} aria-hidden='true' />
        <h3 id={cardHeadingId} className={styles.cardHeading}>
          {copy.cardHeading}
        </h3>
        {/* Two lists, numbered on from each other, so the ladder can sit between step 2
            and step 3 on a phone and beside the steps on a wider screen (.cardBody)
            without the DOM leaving reading order. */}
        <div className={`${styles.cardBody} ${tiers.length > 0 ? '' : styles.cardBodyNoLadder}`}>
          <ol className={`${styles.steps} ${styles.stepsLead}`}>
            <li className={styles.step}>
              <div className={styles.stepRow}>
                <p className={styles.stepText}>{copy.steps.openTab}</p>
                <SitGoTabReplica label={copy.tabLabel} />
              </div>
            </li>
            <li className={styles.step}>
              <p className={styles.stepText}>{copy.steps.pickBuyIn}</p>
            </li>
          </ol>
          {tiers.length > 0 && (
            <table className={styles.ladder}>
              <caption className={styles.srOnly}>{copy.ladder.caption}</caption>
              <thead>
                <tr>
                  <th scope='col' className={styles.ladderHead}>
                    {copy.ladder.buyIn}
                  </th>
                  <th scope='col' className={`${styles.ladderHead} ${styles.ladderHeadPrize}`}>
                    {copy.ladder.prize}
                  </th>
                </tr>
              </thead>
              <tbody>
                {tiers.map((tier) => (
                  <tr key={tier.buyIn} className={styles.ladderRow}>
                    <th scope='row' className={styles.ladderBuyIn}>
                      {tier.buyIn}
                    </th>
                    <td className={styles.ladderPrize}>{tier.prize}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <ol className={`${styles.steps} ${styles.stepsTail}`} start={3}>
            <li className={styles.step}>
              <p className={styles.stepText}>{copy.steps.takeSeat}</p>
            </li>
            <li className={styles.step}>
              <p className={styles.stepText}>{copy.steps.play}</p>
            </li>
          </ol>
        </div>
      </section>

      <p className={styles.message}>
        <Lines lines={content.availability} className={styles.messageLine} />
      </p>
    </>
  );

  const tail = (
    <>
      <button type='button' className={styles.primary} onClick={() => dismiss('find-table')}>
        {copy.action}
      </button>

      <p className={styles.smallPrint}>{content.smallPrint}</p>
    </>
  );

  // Phone: a bottom drawer. Swipe down and a tap outside are the sheet's own dismissals
  // and report as 'backdrop' (a tap outside inside the opening guard is ignored, as on
  // the dialog); the X and Escape report as themselves. The sheet is the dialog, named
  // by the format and the headline, keeps Tab inside and rotates with the
  // portrait-locked lobby (BottomSheet). The content scrolls inside it on a short phone.
  if (asDrawer) {
    return (
      <BottomSheet
        open
        onClose={() => dismiss('backdrop')}
        testId='heads-up-intro-backdrop'
        maxWidth={560}
        ariaLabelledBy={dialogLabel}
        forcePortrait
        backdropGuardMs={backdropGuardMs}>
        <div
          ref={drawerRef}
          className={styles.drawer}
          data-testid='heads-up-intro-drawer'
          tabIndex={-1}
          onKeyDown={handleDrawerKeyDown}>
          <div
            className={styles.drawerScroll}
            onTouchStart={keepScrollFromDragging}
            onTouchMove={keepScrollFromDragging}>
            {lead}
          </div>
          <div className={styles.drawerFooter}>{tail}</div>
        </div>
      </BottomSheet>
    );
  }

  // Tablet and desktop: the centered dialog. It rotates WITH the portrait-locked lobby on
  // touch-landscape, exactly as the promo does (it portals to <body>, so the rotation
  // lives inside the portal). It only ever opens in the lobby, so it never rotates over
  // a landscape table.
  return createPortal(
    <ForcePortrait>
      <div
        className={styles.backdrop}
        data-testid='heads-up-intro-backdrop'
        onClick={handleBackdropClick}>
        <div
          ref={dialogRef}
          className={styles.panel}
          role='dialog'
          aria-modal='true'
          aria-labelledby={dialogLabel}
          tabIndex={-1}
          onClick={stopPropagation}
          onKeyDown={handleKeyDown}>
          <div className={styles.panelScroll}>{lead}</div>
          <div className={styles.panelFooter}>{tail}</div>
        </div>
      </div>
    </ForcePortrait>,
    document.body
  );
};

export default HeadsUpIntroModal;
